import React, { useEffect, useRef, useState, useCallback } from "react";
import { ChevronDown, Phone } from "lucide-react";

const VIDEO_URL_DESKTOP = "/media/hero.mp4";
const VIDEO_URL_MOBILE = "/media/hero-mobile.mp4";
const PHONE = "0435 995 971";

const cues = [
    {
        from: 0.0,
        to: 0.34,
        eyebrow: "FreshEdge Services · Local property care",
        title: "Reliable lawn, garden & outdoor maintenance.",
        sub: "Across Queanbeyan, Canberra, Tuggeranong and surrounding areas — done properly by a small local crew.",
    },
    {
        from: 0.34,
        to: 0.68,
        eyebrow: "Lawn · Garden · Outdoor",
        title: "We mow it. We tidy it. We finish it neat.",
        sub: "From a single mow to a full property cleanup — same crew, same standard, every visit.",
    },
    {
        from: 0.68,
        to: 1.01,
        eyebrow: "Free quotes · Replies usually under 5 hours",
        title: "Send a photo. Get a price. Book the job.",
        sub: "Honest pricing, clear timing, and a tidy finish — that’s the whole pitch.",
    },
];

// Cross-fade opacity for a cue given current scroll progress.
// First cue is solid at progress=0 (no fade-in), last cue holds to the end.
function cueOpacity(progress, cue, isFirst, isLast) {
    const overlap = 0.05;
    if (progress < cue.from && !isFirst) return 0;
    if (progress >= cue.to && !isLast) return 0;
    if (!isFirst && progress < cue.from + overlap) {
        return Math.max(0, (progress - cue.from) / overlap);
    }
    if (!isLast && progress > cue.to - overlap) {
        return Math.max(0, 1 - (progress - (cue.to - overlap)) / overlap);
    }
    return 1;
}

export default function Hero() {
    const wrapRef = useRef(null);
    const videoRef = useRef(null);
    const progressRef = useRef(0);
    const lastFrameTimeRef = useRef(0);
    const [progress, setProgress] = useState(0);
    const [reduceMotion, setReduceMotion] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [bufferedEnough, setBufferedEnough] = useState(false);

    useEffect(() => {
        const mqM = window.matchMedia("(max-width: 768px)");
        const mqR = window.matchMedia("(prefers-reduced-motion: reduce)");
        const mqC = window.matchMedia(
            "(prefers-reduced-data: reduce), (max-width: 480px)"
        );
        setIsMobile(mqM.matches || mqC.matches);
        setReduceMotion(mqR.matches);
        const onM = (e) => setIsMobile(e.matches);
        const onR = (e) => setReduceMotion(e.matches);
        mqM.addEventListener?.("change", onM);
        mqR.addEventListener?.("change", onR);
        return () => {
            mqM.removeEventListener?.("change", onM);
            mqR.removeEventListener?.("change", onR);
        };
    }, []);

    // Track buffered ratio so we don't scrub before frames are decoded
    const onProgress = useCallback(() => {
        const v = videoRef.current;
        if (!v || !v.duration || isNaN(v.duration)) return;
        try {
            if (v.buffered.length > 0) {
                const end = v.buffered.end(v.buffered.length - 1);
                if (end / v.duration >= 0.6) setBufferedEnough(true);
            }
        } catch {
            // Buffered may throw if duration unknown; ignore
        }
    }, []);

    useEffect(() => {
        const v = videoRef.current;
        const wrap = wrapRef.current;
        if (!v || !wrap) return;

        let rafId = null;
        let mounted = true;

        const onMeta = async () => {
            v.muted = true;
            v.playsInline = true;
            if (isMobile || reduceMotion) {
                v.loop = true;
                try {
                    await v.play();
                } catch {
                    /* autoplay blocked — poster stays */
                }
                setBufferedEnough(true);
            } else {
                // Force first-frame paint on desktop scrub mode
                try {
                    await v.play();
                    await new Promise((r) => setTimeout(r, 80));
                    if (!mounted) return;
                    v.pause();
                    v.currentTime = 0.04;
                } catch {
                    v.currentTime = 0.04;
                }
            }
        };

        v.addEventListener("loadedmetadata", onMeta);
        v.addEventListener("progress", onProgress);
        v.addEventListener("canplaythrough", () => setBufferedEnough(true));
        if (v.readyState >= 1) onMeta();
        onProgress();

        // Smooth scroll-scrub loop using frame-rate-independent easing.
        // `target` is the desired video.currentTime derived from scroll progress.
        // We ease toward it with a time-based factor so the same visual smoothness
        // holds at 60Hz and 120Hz displays.
        const tick = (timestamp) => {
            if (!mounted) return;
            const rect = wrap.getBoundingClientRect();
            const total = wrap.offsetHeight - window.innerHeight;
            const passed = Math.min(Math.max(-rect.top, 0), total);
            const p = total > 0 ? passed / total : 0;

            // Only set state if progress changed meaningfully (avoid re-renders)
            if (Math.abs(p - progressRef.current) > 0.001) {
                progressRef.current = p;
                setProgress(p);
            }

            if (
                !isMobile &&
                !reduceMotion &&
                v.duration &&
                bufferedEnough &&
                v.readyState >= 2
            ) {
                const dt = lastFrameTimeRef.current
                    ? Math.min((timestamp - lastFrameTimeRef.current) / 1000, 0.1)
                    : 1 / 60;
                lastFrameTimeRef.current = timestamp;
                const target = p * (v.duration - 0.04);
                const cur = v.currentTime;
                const delta = target - cur;
                // Time-based exponential smoothing — 8 = "smoothness factor"
                const factor = 1 - Math.exp(-dt * 8);
                const next = cur + delta * factor;
                if (Math.abs(delta) > 0.001) {
                    v.currentTime = next;
                }
            }

            rafId = requestAnimationFrame(tick);
        };
        rafId = requestAnimationFrame(tick);

        // Respond to resize so total/offsetHeight stays accurate
        const onResize = () => {
            progressRef.current = -1; // force update next tick
        };
        window.addEventListener("resize", onResize);

        return () => {
            mounted = false;
            window.removeEventListener("resize", onResize);
            v.removeEventListener("loadedmetadata", onMeta);
            v.removeEventListener("progress", onProgress);
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [isMobile, reduceMotion, bufferedEnough, onProgress]);

    return (
        <section
            id="top"
            ref={wrapRef}
            data-testid="hero-scroll-wrap"
            className="relative w-full"
            style={{ height: isMobile ? "120vh" : "300vh" }}
        >
            <div className="sticky top-0 h-screen w-full overflow-hidden bg-[var(--ink)]">
                {/* Painterly fallback — sits BEHIND the video, only visible while
                    the video is decoding or if the codec can't be decoded at all. */}
                <div
                    aria-hidden
                    className="absolute inset-0 z-0"
                    style={{
                        background:
                            "radial-gradient(120% 80% at 70% 25%, #3f5a24 0%, #1f2c12 38%, #0a0f08 72%, #060807 100%)",
                    }}
                />
                <div
                    aria-hidden
                    className="absolute inset-0 z-0 mix-blend-overlay opacity-40 pointer-events-none"
                    style={{
                        background:
                            "repeating-linear-gradient(120deg, rgba(150,200,90,0.18) 0 1px, transparent 1px 9px)",
                    }}
                />

                {/* Hero video — sits above the painterly fallback */}
                <video
                    ref={videoRef}
                    data-testid="hero-video"
                    className="absolute inset-0 w-full h-full object-cover will-change-transform z-[1]"
                    src={isMobile ? VIDEO_URL_MOBILE : VIDEO_URL_DESKTOP}
                    muted
                    playsInline
                    autoPlay
                    preload={isMobile ? "metadata" : "auto"}
                    poster="/media/hero-poster.jpg"
                />

                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/65 pointer-events-none z-10" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(0,0,0,0)_45%,_rgba(0,0,0,0.55)_100%)] pointer-events-none z-10" />
                <div className="grain absolute inset-0 pointer-events-none z-10" />

                {/* Top progress bar */}
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-white/10 z-30">
                    <div
                        className="h-full bg-[var(--terra-soft)]"
                        style={{
                            width: `${Math.round(progress * 100)}%`,
                            transition: "width 80ms linear",
                        }}
                        data-testid="hero-progress"
                    />
                </div>

                {/* Top meta */}
                <div className="absolute top-0 left-0 right-0 z-20 pt-20 md:pt-24">
                    <div className="maxw section-pad flex items-start justify-between text-[var(--paper)]">
                        <div className="flex items-center gap-3 eyebrow-light">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--terra-soft)] animate-pulse" />
                            Taking bookings · This week
                        </div>
                        <div className="hidden md:flex items-center gap-6 eyebrow-light">
                            <span>Queanbeyan · Canberra · Tuggeranong</span>
                            <span className="opacity-50">/</span>
                            <span>Free quotes · usually under 5h reply</span>
                        </div>
                    </div>
                </div>

                {/* Cross-fading cue stack — text only */}
                <div className="absolute inset-0 z-20 flex items-center pointer-events-none">
                    <div className="maxw section-pad w-full">
                        <div className="max-w-3xl">
                            <div
                                className="relative pointer-events-auto"
                                data-testid="hero-cue"
                            >
                                {cues.map((c, i) => {
                                    const o = cueOpacity(
                                        progress,
                                        c,
                                        i === 0,
                                        i === cues.length - 1
                                    );
                                    return (
                                        <div
                                            key={i}
                                            className={
                                                i === 0
                                                    ? "relative"
                                                    : "absolute inset-0 top-0"
                                            }
                                            style={{
                                                opacity: o,
                                                transform: `translateY(${(1 - o) * 14}px)`,
                                                transition:
                                                    "opacity 0.35s ease, transform 0.35s ease",
                                                visibility:
                                                    o > 0.001 ? "visible" : "hidden",
                                                pointerEvents: o > 0.5 ? "auto" : "none",
                                            }}
                                            data-testid={`hero-cue-${i}`}
                                        >
                                            <div className="eyebrow-light mb-5">
                                                {c.eyebrow}
                                            </div>
                                            <h1 className="font-display font-light text-[var(--paper)] text-shadow-soft leading-[0.96] tracking-tight text-4xl sm:text-5xl md:text-7xl lg:text-[80px]">
                                                {c.title}
                                            </h1>
                                            <p className="mt-5 md:mt-6 max-w-xl text-[var(--paper)] text-[15px] md:text-lg leading-relaxed text-shadow-soft font-medium">
                                                {c.sub}
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Always-visible CTAs below the cue stack */}
                            <div className="mt-6 md:mt-9 flex flex-wrap items-center gap-3 pointer-events-auto">
                                <a
                                    href="#contact"
                                    className="btn-paper"
                                    data-testid="hero-cta-quote"
                                >
                                    Get a Free Quote
                                    <span>→</span>
                                </a>
                                <a
                                    href="#services"
                                    className="inline-flex items-center gap-2 px-5 py-3.5 rounded-full text-[#f2ede3] border border-white/30 bg-white/[0.05] hover:bg-white/[0.12] transition text-[14px] font-medium"
                                    data-testid="hero-cta-services"
                                >
                                    View Services
                                </a>
                                <a
                                    href={`tel:${PHONE.replace(/\s/g, "")}`}
                                    className="hidden sm:inline-flex items-center gap-2 px-4 py-3.5 text-[rgba(242,237,227,0.92)] hover:text-[var(--paper)] text-[14px] font-medium transition"
                                    data-testid="hero-cta-call"
                                >
                                    <Phone size={14} />
                                    {PHONE}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom meta */}
                <div className="absolute bottom-0 left-0 right-0 z-20 pb-6 md:pb-8">
                    <div className="maxw section-pad flex items-end justify-between text-[var(--paper)] gap-4 md:gap-6">
                        <div className="hidden md:flex flex-col gap-1">
                            <div className="eyebrow-light">Most booked</div>
                            <div className="font-display text-2xl leading-none">
                                Lawn + edge tidy
                            </div>
                        </div>

                        <div className="flex items-center gap-2 eyebrow-light cue-bob">
                            <ChevronDown size={14} />
                            Scroll to see the services
                        </div>

                        <div className="hidden md:flex flex-col gap-1 items-end">
                            <div className="eyebrow-light">Average reply</div>
                            <div className="font-display text-2xl leading-none">
                                Under 5h
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right timeline rail */}
                <div className="absolute top-1/2 right-6 -translate-y-1/2 hidden lg:flex flex-col gap-3 z-20">
                    {cues.map((c, i) => {
                        const active = progress >= c.from && progress < c.to;
                        return (
                            <div
                                key={i}
                                className={`w-[2px] rounded-full transition-all duration-500 ${
                                    active ? "h-12 bg-[var(--terra-soft)]" : "h-6 bg-white/25"
                                }`}
                            />
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
